/**
 * Real audio-file sound effects (public/sounds/) — the counterpart to the
 * synthesized WebAudio tones in callSdk.ts, which stay for the ONE sound
 * with no suitable free file available: call hangup.
 *
 * Built on the Web Audio API (decode once into an AudioBuffer, then play
 * via a fresh AudioBufferSourceNode each time) rather than `new
 * Audio(...).play()` — the previous approach re-fetched/re-decoded the
 * file on every single play, which is exactly the kind of thing that
 * shows up as "there's a delay" or "sometimes it just doesn't play" on a
 * slower connection or a cold browser cache. Decoding once up front makes
 * every subsequent play instant.
 */

const BASE = `${process.env.PUBLIC_URL || ""}/sounds/`;

export const SOUND = {
  messageSent: "msg_sent.mp3",
  messageReceived: "pop_up.mp3",
  callConnected: "callConnect.mp3",
  personAddedToCall: "adding_new_person_to_call.mp3",
  /* One ringtone for any incoming call, audio or video — the two used to
     be separate files, but the distinction was never something a callee
     could act on differently (the card already says which it is), and a
     single recognisable "someone is calling" tone is easier to learn. */
  incomingCall: "incoming.mp3",
  /* The caller's own ringback. Previously the INCOMING ringtone was
     reused here, so dialling out played the same loud "answer me" tone
     in the caller's own ear — this is a proper outgoing tone instead. */
  outgoingCall: "outgoing.mp3",
  click: "UI_Click.mp3",
  specialNotification: "special_notification.mp3",
} as const;

/* UI click/tap feedback was turned off by request — the little tick on
   every button press became more noise than signal. Disabled centrally
   here (rather than deleting ~30 call sites) so it stays a one-line
   change to reverse: flip this back to false. The SOUND.click key and
   its call sites are left in place, now harmless no-ops (see playOnce). */
const CLICK_SOUND_MUTED = true;

/**
 * Per-file trim range in seconds — cuts leading/trailing silence baked
 * into the source recording, which is what actually causes a sound to
 * feel delayed even once playback itself starts instantly. Numbers found
 * with the waveform clip tool.
 */
export const CLIP: Partial<Record<string, { start: number; end?: number }>> = {
  [SOUND.personAddedToCall]: { start: 0.084, end: 1.0 },
  [SOUND.messageSent]: { start: 0.042, end: 1.0 },
  [SOUND.messageReceived]: { start: 0.062, end: 1.8 },
  // callConnected deliberately untrimmed — same sound for audio and video
  // calls, and the source recording doesn't need clipping.
};

let sharedCtx: AudioContext | null = null;

function ctx(): AudioContext | null {
  try {
    if (!sharedCtx) {
      const Ctx = window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      // "interactive" asks the browser for the smallest output buffer it
      // can manage, i.e. the lowest hardware latency — the right hint for
      // UI feedback sounds, where being a frame late is the whole problem.
      sharedCtx = new Ctx({ latencyHint: "interactive" });
    }
    return sharedCtx;
  } catch {
    return null;
  }
}

// Browsers hold a new AudioContext "suspended" until a user gesture, and
// resume() is asynchronous — so resuming it inside a click handler makes
// that click's own sound wait on the resume. Resuming from a capture-phase
// listener means the context is already running by the time any component's
// onClick runs. Left permanently attached (not `once`) because a context
// can be suspended again later, e.g. when the tab is backgrounded.
if (typeof window !== "undefined") {
  const unlock = () => {
    const c = ctx();
    if (c && c.state === "suspended") void c.resume();
  };
  window.addEventListener("pointerdown", unlock, { capture: true });
  window.addEventListener("keydown", unlock, { capture: true });
}

/** Fully decoded and ready to play with zero async work — see playOnce. */
const decoded = new Map<string, AudioBuffer>();
const inFlight = new Map<string, Promise<AudioBuffer | null>>();

function loadBuffer(file: string): Promise<AudioBuffer | null> {
  const ready = decoded.get(file);
  if (ready) return Promise.resolve(ready);
  let pending = inFlight.get(file);
  if (!pending) {
    const c = ctx();
    pending = !c
      ? Promise.resolve(null)
      : fetch(BASE + file)
          .then((res) => res.arrayBuffer())
          .then((data) => c.decodeAudioData(data))
          .then((buffer) => {
            decoded.set(file, buffer);
            return buffer;
          })
          .catch((e) => {
            console.warn(`sound "${file}" failed to load`, e);
            return null;
          });
    inFlight.set(file, pending);
  }
  return pending;
}

// Decode the most frequently triggered sounds as soon as this module
// loads, so the very first click/message of the session already has a
// ready-to-play buffer instead of paying the fetch+decode cost right when
// the sound is needed. The call-related sounds load lazily on first use
// instead — a call always takes a few seconds to ring/connect, which is
// plenty of time to decode, so there's no benefit to spending bytes on
// them before anyone has even opened the app's calling feature.
[SOUND.messageSent, SOUND.messageReceived].forEach((file) => void loadBuffer(file));

/** Schedules an already-decoded buffer. Synchronous on purpose — see
 * playOnce. `volume` (0–1) routes through a gain node for sounds that
 * should be quieter than the source recording. */
function startBuffer(file: string, buffer: AudioBuffer, volume = 1): void {
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") void c.resume();
  try {
    const clip = CLIP[file];
    const start = clip?.start ?? 0;
    const duration = clip?.end != null ? Math.max(0, clip.end - start) : undefined;
    const source = c.createBufferSource();
    source.buffer = buffer;
    if (volume < 1) {
      const gain = c.createGain();
      gain.gain.value = Math.max(0, volume);
      source.connect(gain).connect(c.destination);
    } else {
      source.connect(c.destination);
    }
    source.start(0, start, duration);
  } catch (e) {
    console.warn(`sound "${file}" failed to play`, e);
  }
}

/**
 * Plays a one-shot effect. Multiple overlapping calls (e.g. two messages
 * arriving in quick succession) each get their own source node, so they
 * layer instead of cutting each other off.
 *
 * The already-decoded path deliberately avoids promises entirely. A click
 * is a "discrete" React event, so React flushes the re-render it triggers
 * SYNCHRONOUSLY at the end of the handler — before any microtask queued
 * during that handler gets to run. Starting playback from a `.then()`
 * therefore waited on the entire re-render (easily 100ms+ on a tree this
 * size), which is what made every sound feel noticeably late even though
 * the audio was already decoded and sitting in memory.
 */
export function playOnce(file: string, volume = 1): void {
  if (CLICK_SOUND_MUTED && file === SOUND.click) return;
  const ready = decoded.get(file);
  if (ready) {
    startBuffer(file, ready, volume); // no await, no microtask — plays this tick
    return;
  }
  void loadBuffer(file).then((buffer) => {
    if (buffer) startBuffer(file, buffer, volume);
  });
}

let activeLoop: AudioBufferSourceNode | null = null;
let loopToken = 0;
let loopCapTimer: number | null = null;

/**
 * Last-resort cap on how long any looping sound may play. Ringtones are
 * normally stopped by the call state machine, but that machine depends on
 * signalling arriving from another device — so a dropped connection, a
 * closed caller tab, or a state bug could otherwise leave a ringtone
 * looping indefinitely with no way to silence it short of closing the
 * tab. Comfortably longer than the longest real ring (invite expiry, 45s)
 * so it never cuts a legitimate one short.
 */
const MAX_LOOP_MS = 60_000;

/** Starts a looping sound (ringtones) — stops whatever loop was already
 * playing first, so switching e.g. incoming-audio → incoming-video never
 * overlaps two ringtones. */
export function startLoop(file: string): void {
  stopLoop();
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") void c.resume();
  const myToken = ++loopToken;

  const begin = (buffer: AudioBuffer) => {
    try {
      const source = c.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(c.destination);
      source.start(0);
      activeLoop = source;
      loopCapTimer = window.setTimeout(() => {
        console.warn(`sound "${file}" hit the max loop duration — stopping`);
        stopLoop();
      }, MAX_LOOP_MS);
    } catch (e) {
      console.warn(`sound "${file}" failed to loop`, e);
    }
  };

  const ready = decoded.get(file);
  if (ready) {
    begin(ready); // same synchronous fast path as playOnce
    return;
  }
  void loadBuffer(file).then((buffer) => {
    // A newer startLoop/stopLoop call already superseded this one while
    // the buffer was loading — don't let a late callback resurrect it.
    if (!buffer || myToken !== loopToken) return;
    begin(buffer);
  });
}

export function stopLoop(): void {
  loopToken++; // invalidate any in-flight startLoop callback
  if (loopCapTimer !== null) {
    window.clearTimeout(loopCapTimer);
    loopCapTimer = null;
  }
  if (activeLoop) {
    try {
      activeLoop.stop();
    } catch {
      /* already stopped */
    }
    activeLoop = null;
  }
}
